import { useState, useEffect } from "react";
import { fetchImageUrl } from "../network/api";
import { constructPayload, getDateRange } from "../utils/helper";
import { getCountsData, getMavisSmartViewRequestData } from "./helpers";
import {
  getAdvancedSearchQuery,
  getSalesActivitySearchQuery,
  getLeadTypeSearchQuery,
  getSVTabCode,
  getTaskType,
  SMARTVIEW_TYPE_MAPPING,
} from "../utils/widgetHelper";

export const fetchImage = async (setWidgetData, config) => {
  if (config?.image) {
    const imageUrlData = await fetchImageUrl(config.image.imageKey);
    if (imageUrlData) {
      setWidgetData((prevData) => ({
        ...prevData,
        imageURL: imageUrlData,
      }));
    }
  }
};

export const fetchTasks = async (
  config,
  forceRefresh,
  setWidgetData,
  activeDashboardId,
  widget
) => {
  let postBody = constructPayload({ config });
  if (config.dateFilters && config.dateFilters.id) {
    const dateRange = getDateRange(config.dateFilters.id);
    postBody.filters = {
      ...postBody.filters,
      ...dateRange,
    };
  }
  const getTotalCount =
    !config.setThresholds &&
    !config?.max &&
    config.taskStatus.id === "completed";
  const taskCountData = await getCountsData(
    forceRefresh,
    widget,
    activeDashboardId,
    postBody,
    getTotalCount
  );
  // If maxCount is not provided, fetch it
  if (getTotalCount) {
    setWidgetData((prevData) => ({
      ...prevData,
      ...taskCountData,
    }));
  } else if (
    !config.setThresholds &&
    !config?.max &&
    config.taskStatus.id !== "completed"
  ) {
    setWidgetData((prevData) => ({
      ...prevData,
      count: taskCountData?.count,
      maxCount: false,
    }));
  } else {
    setWidgetData((prevData) => ({
      ...prevData,
      count: taskCountData?.count,
      maxCount: config?.max,
    }));
  }
};

const useWidgetData = (
  widget,
  smartviewsConfig,
  activeDashboardId,
  forceRefresh
) => {
  const { id, widgetType: type, config } = widget;

  const [widgetData, setWidgetData] = useState(config);
  const [initialLoad, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const loadData = async (forceRefresh = false) => {
    setLoading(true);
    try {
      if (config && config.entity && config.entity.id === "tasks")
        await fetchTasks(
          config,
          forceRefresh,
          setWidgetData,
          activeDashboardId,
          widget
        );

      if (config && config.entity && config.entity.id === "smartviews") {
        if (smartviewsConfig && smartviewsConfig.tabs) {
          const matchedTab = smartviewsConfig.tabs.find(
            (tab) => tab.id === config.smartViewTab.id
          );

          let mappedText = SMARTVIEW_TYPE_MAPPING[matchedTab.type];
          let leadTypeConfiguration = matchedTab?.tabContentConfigurationResponseDto?.fetchCriteria?.leadTypeConfiguration
                ? getLeadTypeSearchQuery(matchedTab.tabContentConfigurationResponseDto.fetchCriteria.leadTypeConfiguration)
                : null;

          // Construct the post body for the API call
          let postBody = {
            advancedSearch: getAdvancedSearchQuery(matchedTab),
            customFilters: leadTypeConfiguration,
            pageIndex: 1,
            pageSize: 1,
          };

          switch (mappedText) {
            case "tasks":
              postBody.pageIndex = 0;
              postBody.pageSize = 0;
              postBody.type = getTaskType(matchedTab)
              break;
            case "activities":
              postBody.code = getSVTabCode(matchedTab);
              postBody.customFilters = postBody.code ? getSalesActivitySearchQuery(postBody.code, leadTypeConfiguration) : null;
              postBody.isOpportunity = false;
              break;
            case "opportunity":
              postBody.code = getSVTabCode(matchedTab);
              postBody.isOpportunity = true;
              mappedText = "activities";
              break;
            case "accounts":
              postBody.type = getSVTabCode(matchedTab);
              break;
            case "custom_entity":
              postBody = getMavisSmartViewRequestData(matchedTab);
              break;
            default:
              break;
          }
        
          // Fetch smart views count with the mapped text as an argument
          const taskCountData = await getCountsData(
            forceRefresh,
            widget,
            activeDashboardId,
            postBody,
            false,
            mappedText
          );
          setWidgetData((prevData) => ({
            ...prevData,
            ...taskCountData,
            maxCount: config?.max,
          }));
        }
      }

      // Fetch image URL if applicable
      await fetchImage(setWidgetData, config);
    } catch (error) {
      console.error(`Error loading data for widget ${id}:`, error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [id, type, config, smartviewsConfig]);
  useEffect(() => {
    if (initialLoad) setInitialLoading(false);
    else {
      loadData(true);
    }
  }, [forceRefresh]);

  return { widgetData, loading };
};

export default useWidgetData;
